/**
 * OmegaConsolePage — `/omega/console`.
 *
 * Phase 20 Founder Command Console. Admin-only, gated by isOmegaAdmin().
 * Not discoverable publicly — no nav entry, no link from any public page.
 *
 * Layout:
 *   [Header: Breadcrumbs + Quick Actions Toolbar]
 *   [Grid: Left Rail | Center Col | Right Rail]   (lg: 3-col, mobile: 1-col)
 *   [Bottom: Founder Action Log full-width]
 *
 * Keyboard shortcuts (meta = Cmd/Ctrl):
 *   ⌘A → approve focused decision
 *   ⌘D → defer focused decision
 *   ⌘R → reject focused decision
 *   ⌘K → open command palette
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProposalsResponse } from '@/api/types';
import { sha256 } from '@noble/hashes/sha2';
import { bytesToHex } from '@noble/hashes/utils';

import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { getAccessToken } from '@/api/supabase';
import { cn } from '@/utils/cn';
import { canonicalJsonStringify } from '@/api/mock/sign';
import { useBrainSummaries, omegaQueryKeys } from '@/features/omega/dashboard';
import { useDecisions } from '@/features/omega/dashboard/hooks/useOmegaState';
import { BRAINS_ORDERED } from '@/features/omega/dashboard/data/brainRegistry';
import type { BrainSummary, BrainId, PendingDecision } from '@/features/omega/dashboard/data/types';

// ─── Types ────────────────────────────────────────────────────────

interface LogEntry {
  seq: number;
  entry_type: string;
  payload_json?: Record<string, unknown>;
}

interface LogTailResponse {
  entries: LogEntry[];
  total: number;
}

interface LaunchPlan {
  id: string;
  title: string;
  status: string;
  revenue_projected_usd: number | null;
}

interface LaunchPlansResponse {
  plans: LaunchPlan[];
  total_projected_usd: number | null;
}

type QuickActionKind = 'emergency-stop' | 'fire-production';

// ─── API helpers ──────────────────────────────────────────────────

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim() ??
  'http://localhost:3000';

async function authedPost<T>(path: string, body: unknown): Promise<T> {
  const resp = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json, application/problem+json',
    },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const text = await resp.text();
    let detail = text;
    try {
      detail = (JSON.parse(text) as { detail?: string }).detail ?? text;
    } catch {}
    throw new Error(detail || `HTTP ${resp.status}`);
  }
  return resp.json() as Promise<T>;
}

// Founder-gated reads (e.g. /omega/log/tail) require an Authorization bearer:
// the live Supabase JWT (founder email) verified by `require_founder`, with a
// dev fallback. Sending only `credentials: 'include'` (cookies) is NOT accepted
// by the server's auth gate, which was the cause of "Failed to load log".
const DEV_BEARER =
  (import.meta.env.VITE_API_BEARER_TOKEN as string | undefined) ?? 'dev-operator-token';

async function authedFetch<T>(path: string): Promise<T> {
  const jwt = await getAccessToken();
  const bearer = jwt ?? DEV_BEARER;
  const resp = await fetch(`${API_BASE}${path}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${bearer}`,
    },
    credentials: 'include',
  });
  if (!resp.ok) {
    throw new Error(`HTTP ${resp.status}`);
  }
  return resp.json() as Promise<T>;
}

/** Humanise a snake_case log entry type into a plain sentence-case description. */
function humanizeEntryType(entryType: string): string {
  const s = entryType.replace(/_/g, ' ').trim();
  return s.length === 0 ? 'Log entry' : s.charAt(0).toUpperCase() + s.slice(1);
}

/** Best-effort timestamp from a log entry's payload (no top-level ts on the wire). */
function entryTimestamp(e: LogEntry): string | null {
  const p = e.payload_json;
  if (!p) return null;
  for (const key of [
    'fired_at',
    'incurred_at',
    'received_at',
    'recorded_at',
    'generated_at',
    'created_at',
    'timestamp',
    'at',
  ]) {
    const v = p[key];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return null;
}

/** Short, local human time for a log line; empty string when unknown. */
function formatEntryTime(e: LogEntry): string {
  const iso = entryTimestamp(e);
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

async function founderPost<T>(
  path: string,
  body: unknown,
  founderSig: string,
): Promise<T> {
  const bodyStr = canonicalJsonStringify(body);
  const resp = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json, application/problem+json',
      'X-Founder-Signature': founderSig,
    },
    body: bodyStr,
  });
  if (!resp.ok) {
    const text = await resp.text();
    let detail = text;
    try {
      detail = (JSON.parse(text) as { detail?: string }).detail ?? text;
    } catch {}
    throw new Error(detail || `HTTP ${resp.status}`);
  }
  return resp.json() as Promise<T>;
}

// ─── Page ─────────────────────────────────────────────────────────

export function OmegaConsolePage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;
  return <ConsoleInner />;
}

// ─── ConsoleInner ─────────────────────────────────────────────────

function ConsoleInner() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [focusedDecisionId, setFocusedDecisionId] = useState<string | null>(null);
  const [decisionError, setDecisionError] = useState<string | undefined>();
  const [quickAction, setQuickAction] = useState<QuickActionKind | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [approveAllBusy, setApproveAllBusy] = useState(false);

  const { data: decisionsData } = useDecisions();
  const decisions = decisionsData?.decisions ?? [];

  const doDecisionAction = useMemo(
    () =>
      async (id: string, action: 'approve' | 'defer' | 'reject') => {
        setDecisionError(undefined);
        try {
          await authedPost<unknown>(`/api/v1/admin/decision/${id}/${action}`, {});
          await qc.invalidateQueries({ queryKey: omegaQueryKeys.decisions() });
        } catch (e) {
          setDecisionError(e instanceof Error ? e.message : String(e));
        }
      },
    [qc],
  );

  const approveAllLowRisk = useMemo(
    () => async () => {
      const eligible = decisions.filter((d) => d.confidence === 'high');
      if (eligible.length === 0) return;
      setApproveAllBusy(true);
      setDecisionError(undefined);
      try {
        await Promise.all(
          eligible.map((d) =>
            authedPost<unknown>(`/api/v1/admin/decision/${d.decision_id}/approve`, {}),
          ),
        );
        await qc.invalidateQueries({ queryKey: omegaQueryKeys.decisions() });
      } catch (e) {
        setDecisionError(e instanceof Error ? e.message : String(e));
      } finally {
        setApproveAllBusy(false);
      }
    },
    [decisions, qc],
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      if (e.key === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
      if (!focusedDecisionId) return;
      if (e.key === 'a') {
        e.preventDefault();
        void doDecisionAction(focusedDecisionId, 'approve');
      } else if (e.key === 'd') {
        e.preventDefault();
        void doDecisionAction(focusedDecisionId, 'defer');
      } else if (e.key === 'r') {
        e.preventDefault();
        void doDecisionAction(focusedDecisionId, 'reject');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [focusedDecisionId, doDecisionAction]);

  const { data: proposalsData } = useQuery<ProposalsResponse>({
    queryKey: ['console', 'proposals'],
    queryFn: () => authedFetch<ProposalsResponse>('/api/v1/omega/proposals'),
    refetchInterval: 30_000,
    staleTime: 25_000,
  });
  const pendingProposals = proposalsData?.pending ?? 0;

  const highConfidenceCount = decisions.filter((d) => d.confidence === 'high').length;

  return (
    <div className="flex flex-col gap-5 pb-8">
      {/* Header */}
      <header className="flex flex-col gap-3">
        <Breadcrumbs
          crumbs={[{ label: 'OMEGA', to: '/omega' }, { label: 'Console' }]}
        />
        <h1 className="sr-only">Founder Command Console</h1>

        {/* Quick actions toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setQuickAction('emergency-stop')}
            className="rounded-md border border-danger/50 bg-danger/10 px-3 py-1.5 text-xs font-semibold text-danger hover:bg-danger/20"
          >
            Pause everything
          </button>
          <button
            type="button"
            onClick={() => setQuickAction('fire-production')}
            className="rounded-md border border-spark/50 bg-spark/10 px-3 py-1.5 text-xs font-semibold text-spark hover:bg-spark/20"
          >
            Fire production now
          </button>
          <button
            type="button"
            onClick={() => void approveAllLowRisk()}
            disabled={highConfidenceCount === 0 || approveAllBusy}
            className={cn(
              'rounded-md border px-3 py-1.5 text-xs font-semibold',
              highConfidenceCount > 0 && !approveAllBusy
                ? 'border-success/50 bg-success/10 text-success hover:bg-success/20'
                : 'cursor-not-allowed border-border bg-canvas text-ink-subtle opacity-50',
            )}
          >
            {approveAllBusy ? 'Approving…' : `Approve all low-risk${highConfidenceCount > 0 ? ` (${highConfidenceCount})` : ''}`}
          </button>
          <button
            type="button"
            onClick={() => navigate('/omega/activity')}
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs text-ink-muted hover:text-ink"
          >
            Daily briefing
          </button>
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-mono text-ink-muted hover:text-ink"
          >
            ⌘K
          </button>
        </div>
      </header>

      {/* Proposals awaiting badge */}
      {pendingProposals > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-4 py-3">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white">
            {pendingProposals}
          </span>
          <p className="flex-1 text-sm font-medium text-amber-900 dark:text-amber-200">
            OMEGA Brain proposal{pendingProposals !== 1 ? 's' : ''} awaiting your review
          </p>
          <button
            type="button"
            onClick={() => {
              if (decisions[0]) setFocusedDecisionId(decisions[0].decision_id);
              document
                .getElementById('decisions-panel')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className="text-xs font-semibold text-amber-700 dark:text-amber-300 hover:underline"
          >
            Review →
          </button>
        </div>
      )}

      {/* Main grid */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[200px_1fr_200px]">
        <BrainRail />
        <div id="decisions-panel" className="flex flex-col gap-4 scroll-mt-4">
          <DecisionsPanel
            decisions={decisions}
            focusedId={focusedDecisionId}
            error={decisionError}
            onFocus={setFocusedDecisionId}
            onAction={doDecisionAction}
          />
          <ExecutionsFeed />
        </div>
        <RightRail />
      </div>

      {/* Bottom: Founder Action Log */}
      <FounderActionLog />

      {/* FounderSignDialog */}
      {quickAction && (
        <FounderSignDialog
          action={quickAction}
          onSign={async (sig, nonce) => {
            if (quickAction === 'emergency-stop') {
              await founderPost('/api/v1/admin/defense/emergency-stop', { nonce }, sig);
            } else {
              await founderPost('/api/v1/admin/production/fire-now', { nonce }, sig);
            }
            await qc.invalidateQueries({ queryKey: omegaQueryKeys.all });
          }}
          onClose={() => setQuickAction(null)}
        />
      )}

      {/* Command Palette */}
      {paletteOpen && (
        <CommandPalette
          decisions={decisions}
          onDecisionAction={doDecisionAction}
          onClose={() => setPaletteOpen(false)}
        />
      )}
    </div>
  );
}

// ─── BrainRail ────────────────────────────────────────────────────

function BrainRail() {
  const { data } = useBrainSummaries();
  const brains = data ?? [];

  const byId = useMemo(() => {
    const m = new Map<BrainId, BrainSummary>();
    for (const b of brains) m.set(b.id, b);
    return m;
  }, [brains]);

  return (
    <nav aria-label="Brain rail" className="flex flex-col gap-1">
      <div className="mb-1 px-2 text-[10px] uppercase tracking-wider text-ink-subtle">
        Brains
      </div>
      {BRAINS_ORDERED.map((reg) => {
        const summary = byId.get(reg.id);
        const status = summary?.status ?? 'offline';
        const decisionsMin = summary?.decisions_last_min ?? 0;
        const alerts = summary?.active_alerts ?? 0;

        return (
          <Link
            key={reg.id}
            to={`/omega/brain/${reg.id}`}
            className="flex flex-col gap-0.5 rounded-md px-2 py-1.5 text-xs hover:bg-surface"
          >
            <div className="flex items-center gap-1.5">
              <StatusDot status={status} />
              <span className="font-medium text-ink">{reg.short_name}</span>
              {alerts > 0 && (
                <span className="ms-auto font-mono text-[10px] text-danger">
                  {alerts}▲
                </span>
              )}
            </div>
            <div className="ps-3 text-[10px] text-ink-muted">
              {decisionsMin} dec/min
            </div>
          </Link>
        );
      })}
    </nav>
  );
}

function StatusDot({ status }: { status: string }) {
  const color: Record<string, string> = {
    healthy: 'bg-success',
    live: 'bg-success',
    degraded: 'bg-warning',
    critical: 'bg-danger',
    paused: 'bg-warning',
    offline: 'bg-ink-subtle',
    blueprint: 'bg-ink-muted',
  };
  return (
    <span
      className={cn('h-1.5 w-1.5 shrink-0 rounded-full', color[status] ?? 'bg-ink-subtle')}
      aria-hidden
    />
  );
}

// ─── DecisionsPanel ───────────────────────────────────────────────

function DecisionsPanel({
  decisions,
  focusedId,
  error,
  onFocus,
  onAction,
}: {
  decisions: PendingDecision[];
  focusedId: string | null;
  error: string | undefined;
  onFocus: (id: string | null) => void;
  onAction: (id: string, action: 'approve' | 'defer' | 'reject') => void;
}) {
  return (
    <section
      aria-label="Pending decisions"
      className="rounded-xl border border-border bg-surface"
    >
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <h2 className="flex-1 text-xs font-semibold uppercase tracking-wider text-ink-subtle">
          Pending Decisions
        </h2>
        <span className="font-mono text-[10px] text-ink-muted">{decisions.length}</span>
      </div>

      {error && (
        <div className="mx-3 mt-3 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">
          {error}
        </div>
      )}

      <div className="flex flex-col divide-y divide-border">
        {decisions.length === 0 && (
          <div className="flex h-16 items-center justify-center text-xs text-ink-subtle">
            No pending decisions.
          </div>
        )}
        {decisions.map((d) => {
          const focused = d.decision_id === focusedId;
          return (
            <div
              key={d.decision_id}
              className={cn(
                'cursor-pointer px-4 py-3 transition-colors',
                focused ? 'bg-canvas' : 'hover:bg-canvas/50',
              )}
              onClick={() => onFocus(focused ? null : d.decision_id)}
            >
              <div className="flex items-start gap-2">
                <RecommendationBadge recommendation={d.recommendation} />
                <ConfidenceBadge confidence={d.confidence} />
                <span className="flex-1 text-xs text-ink">{d.question}</span>
              </div>

              {focused && (
                <div className="mt-3 flex flex-col gap-3">
                  {d.reasoning.length > 0 && (
                    <ul className="flex flex-col gap-0.5 ps-2">
                      {d.reasoning.map((r, i) => (
                        <li key={i} className="text-[11px] text-ink-muted">
                          · {r}
                        </li>
                      ))}
                    </ul>
                  )}
                  {d.risks.length > 0 && (
                    <ul className="flex flex-col gap-0.5 ps-2">
                      {d.risks.map((r, i) => (
                        <li key={i} className="text-[11px] text-warning">
                          ⚠ {r}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAction(d.decision_id, 'approve');
                      }}
                      className="rounded border border-success/50 bg-success/10 px-2.5 py-1 text-[11px] font-semibold text-success hover:bg-success/20"
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAction(d.decision_id, 'defer');
                      }}
                      className="rounded border border-border bg-canvas px-2.5 py-1 text-[11px] text-ink-muted hover:text-ink"
                    >
                      Defer
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAction(d.decision_id, 'reject');
                      }}
                      className="rounded border border-danger/50 bg-danger/10 px-2.5 py-1 text-[11px] font-semibold text-danger hover:bg-danger/20"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function RecommendationBadge({
  recommendation,
}: {
  recommendation: 'approve' | 'defer' | 'reject';
}) {
  const styles: Record<string, string> = {
    approve: 'border-success/50 bg-success/10 text-success',
    defer: 'border-border bg-canvas text-ink-muted',
    reject: 'border-danger/50 bg-danger/10 text-danger',
  };
  return (
    <span
      className={cn(
        'shrink-0 rounded border px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase',
        styles[recommendation],
      )}
    >
      {recommendation}
    </span>
  );
}

function ConfidenceBadge({ confidence }: { confidence: 'low' | 'medium' | 'high' }) {
  const styles: Record<string, string> = {
    low: 'text-danger',
    medium: 'text-warning',
    high: 'text-success',
  };
  return (
    <span className={cn('shrink-0 text-[10px]', styles[confidence])}>
      {confidence}
    </span>
  );
}

// ─── ExecutionsFeed ───────────────────────────────────────────────

function ExecutionsFeed() {
  const { data, isPending, isError } = useQuery<LogTailResponse>({
    queryKey: ['console', 'log-tail', 20],
    queryFn: () => authedFetch<LogTailResponse>('/api/v1/omega/log/tail?limit=20'),
    refetchInterval: 5_000,
    staleTime: 4_000,
  });

  const entries = data?.entries ?? [];

  return (
    <section
      aria-label="Executions feed"
      className="rounded-xl border border-border bg-surface"
    >
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <h2 className="flex-1 text-xs font-semibold uppercase tracking-wider text-ink-subtle">
          Executions
        </h2>
        <span className="font-mono text-[10px] text-ink-muted">5s</span>
      </div>

      <div className="flex flex-col gap-0.5 p-3">
        {isPending && (
          <div className="flex h-12 items-center justify-center text-xs text-ink-subtle">
            Loading…
          </div>
        )}
        {isError && (
          <div className="flex h-12 items-center justify-center text-xs text-danger">
            Failed to load log.
          </div>
        )}
        {!isPending && !isError && entries.length === 0 && (
          <div className="flex h-12 items-center justify-center text-xs text-ink-subtle">
            No entries yet.
          </div>
        )}
        {entries.map((e) => (
          <div
            key={e.seq}
            className="flex items-center gap-2 rounded px-2 py-1 hover:bg-canvas"
          >
            <span className="font-mono text-[10px] tabular-nums text-ink-subtle">
              #{e.seq}
            </span>
            {formatEntryTime(e) && (
              <span className="shrink-0 font-mono text-[10px] tabular-nums text-ink-muted">
                {formatEntryTime(e)}
              </span>
            )}
            <span className="flex-1 text-[11px] text-ink">
              {humanizeEntryType(e.entry_type)}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── RightRail ────────────────────────────────────────────────────

function RightRail() {
  const { data } = useQuery<LaunchPlansResponse>({
    queryKey: ['console', 'launchplans'],
    queryFn: () =>
      authedFetch<LaunchPlansResponse>('/api/v1/production/launchplans').catch(
        () => ({ plans: [], total_projected_usd: null }),
      ),
    refetchInterval: 30_000,
    staleTime: 25_000,
  });

  const plans = data?.plans ?? [];
  const totalRevenue = data?.total_projected_usd ?? null;

  return (
    <aside aria-label="Launch plans" className="flex flex-col gap-1">
      <div className="mb-1 px-2 text-[10px] uppercase tracking-wider text-ink-subtle">
        Launch Plans
      </div>

      {plans.length === 0 && (
        <div className="rounded-md border border-border bg-surface px-3 py-3 text-[11px] text-ink-subtle">
          No launch plans yet.
        </div>
      )}

      {plans.map((p) => (
        <div
          key={p.id}
          className="rounded-md border border-border bg-surface px-3 py-2"
        >
          <div className="text-[11px] font-medium text-ink">{p.title}</div>
          <div className="mt-0.5 flex items-center gap-1.5">
            <span className="font-mono text-[10px] text-ink-muted">{p.status}</span>
            {p.revenue_projected_usd != null && (
              <span className="ms-auto font-mono text-[10px] text-success">
                ${p.revenue_projected_usd.toLocaleString()}
              </span>
            )}
          </div>
        </div>
      ))}

      {totalRevenue != null && plans.length > 0 && (
        <div className="mt-1 rounded-md border border-border bg-canvas px-3 py-2">
          <div className="text-[10px] text-ink-muted">Total projected</div>
          <div className="font-mono text-xs font-semibold text-success">
            ${totalRevenue.toLocaleString()}
          </div>
        </div>
      )}
    </aside>
  );
}

// ─── FounderActionLog ─────────────────────────────────────────────

const FOUNDER_ENTRY_TYPES = new Set([
  'omega_genesis',
  'constitution_ratification',
  'refuse_list_update',
  'trigger_created',
  'trigger_deleted',
  'emergency_stop',
  'emergency_resume',
  'decision_approved',
  'decision_rejected',
  'decision_deferred',
]);

function FounderActionLog() {
  const { data, isPending, isError } = useQuery<LogTailResponse>({
    queryKey: ['console', 'log-tail', 100],
    queryFn: () => authedFetch<LogTailResponse>('/api/v1/omega/log/tail?limit=100'),
    refetchInterval: 30_000,
    staleTime: 25_000,
  });

  const entries = (data?.entries ?? [])
    .filter((e) => FOUNDER_ENTRY_TYPES.has(e.entry_type))
    .slice(-10)
    .reverse();

  return (
    <section
      aria-label="Founder action log"
      className="rounded-xl border border-border bg-surface"
    >
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <h2 className="flex-1 text-xs font-semibold uppercase tracking-wider text-ink-subtle">
          Founder Action Log
        </h2>
        <span className="font-mono text-[10px] text-ink-muted">last 10 founder entries</span>
      </div>

      <div className="flex flex-col gap-0.5 p-3">
        {isPending && (
          <div className="flex h-12 items-center justify-center text-xs text-ink-subtle">
            Loading…
          </div>
        )}
        {isError && (
          <div className="flex h-12 items-center justify-center text-xs text-danger">
            Failed to load founder log.
          </div>
        )}
        {!isPending && !isError && entries.length === 0 && (
          <div className="flex h-12 items-center justify-center text-xs text-ink-subtle">
            No founder actions recorded.
          </div>
        )}
        {entries.map((e) => (
          <div
            key={e.seq}
            className="flex items-center gap-3 rounded px-2 py-1.5 hover:bg-canvas"
          >
            <span className="font-mono text-[10px] tabular-nums text-ink-subtle">
              #{e.seq}
            </span>
            {formatEntryTime(e) && (
              <span className="shrink-0 font-mono text-[10px] tabular-nums text-ink-muted">
                {formatEntryTime(e)}
              </span>
            )}
            <span className="flex-1 text-[11px] text-ink">
              {humanizeEntryType(e.entry_type)}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── FounderSignDialog ────────────────────────────────────────────

function FounderSignDialog({
  action,
  onSign,
  onClose,
}: {
  action: QuickActionKind;
  onSign: (sig: string, nonce: string) => Promise<void> | void;
  onClose: () => void;
}) {
  const nonce = useMemo(() => crypto.randomUUID(), []);
  const body = { nonce };
  const bodyStr = canonicalJsonStringify(body);
  const hashHex = bytesToHex(sha256(new TextEncoder().encode(bodyStr)));
  const hashString = `sha256:${hashHex}`;

  const label =
    action === 'emergency-stop' ? 'Pause Everything (Emergency Stop)' : 'Fire Production Now';

  const [sig, setSig] = useState('');
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | undefined>();

  const copy = () => {
    void navigator.clipboard.writeText(hashString);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const submit = async () => {
    setBusy(true);
    setErr(undefined);
    try {
      await onSign(sig.trim(), nonce);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-label="Founder sign dialog"
    >
      <div className="flex w-full max-w-lg flex-col gap-5 rounded-xl border border-border bg-surface p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-ink">{label}</h2>
          <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink">
            ✕
          </button>
        </div>

        <p className="text-sm text-ink-muted">
          Sign the hash below with your founder Ed25519 key and paste the result.
        </p>

        <div className="flex flex-col gap-2">
          <label className="text-[10px] uppercase tracking-wider text-ink-subtle">
            Canonical payload (JCS JSON)
          </label>
          <pre className="max-h-24 overflow-auto rounded-md border border-border bg-canvas px-3 py-2 font-mono text-[11px] text-ink-muted">
            {bodyStr}
          </pre>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-[10px] uppercase tracking-wider text-ink-subtle">
            Hash to sign
          </label>
          <div className="flex items-center gap-2 rounded-md border border-border bg-canvas px-3 py-2">
            <code className="flex-1 break-all font-mono text-[11px] text-ink">
              {hashString}
            </code>
            <button
              type="button"
              onClick={copy}
              className="shrink-0 rounded border border-border px-2 py-0.5 text-[10px] text-ink-muted hover:text-ink"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label
            htmlFor="console-sig-input"
            className="text-[10px] uppercase tracking-wider text-ink-subtle"
          >
            Signature (ed25519:&lt;base64&gt;)
          </label>
          <input
            id="console-sig-input"
            type="text"
            placeholder="ed25519:<base64-signature>"
            value={sig}
            onChange={(e) => setSig(e.target.value)}
            className="rounded-md border border-border bg-canvas px-3 py-2 font-mono text-[11px] text-ink placeholder:text-ink-muted focus:outline-none focus:ring-1 focus:ring-spark"
          />
        </div>

        {err && (
          <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">
            {err}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-md border border-border px-4 py-2 text-sm text-ink-muted hover:text-ink"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!sig.trim() || busy}
            className={cn(
              'flex-1 rounded-md px-4 py-2 text-sm font-semibold',
              action === 'emergency-stop'
                ? 'bg-danger text-white hover:bg-danger/90'
                : 'bg-spark text-white hover:bg-spark/90',
              (!sig.trim() || busy) && 'cursor-not-allowed opacity-50',
            )}
          >
            {busy ? 'Submitting…' : label}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CommandPalette ───────────────────────────────────────────────

const PALETTE_ACTIONS = [
  { label: 'Daily briefing', to: '/omega/activity' },
  { label: 'Threat feed', to: '/omega/defense' },
  { label: 'OMEGA dashboard', to: '/omega' },
  { label: 'Triggers', to: '/omega/triggers' },
  { label: 'Log explorer', to: '/omega/log' },
];

function CommandPalette({
  decisions,
  onDecisionAction,
  onClose,
}: {
  decisions: PendingDecision[];
  onDecisionAction: (id: string, action: 'approve' | 'defer' | 'reject') => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const q = query.toLowerCase();

  const filteredBrains = BRAINS_ORDERED.filter(
    (b) => !q || b.short_name.toLowerCase().includes(q) || b.id.includes(q),
  );

  const filteredDecisions = decisions.filter(
    (d) => !q || d.question.toLowerCase().includes(q),
  );

  const filteredActions = PALETTE_ACTIONS.filter(
    (a) => !q || a.label.toLowerCase().includes(q),
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 pt-24"
      role="dialog"
      aria-label="Command palette"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-xl flex-col rounded-xl border border-border bg-surface shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-border px-4 py-3">
          <input
            autoFocus
            type="text"
            placeholder="Search brains, decisions, actions…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full bg-transparent text-sm text-ink placeholder:text-ink-muted focus:outline-none"
          />
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-2">
          {filteredBrains.length > 0 && (
            <PaletteGroup label="Brains">
              {filteredBrains.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-start text-sm text-ink hover:bg-canvas"
                  onClick={() => {
                    navigate(`/omega/brain/${b.id}`);
                    onClose();
                  }}
                >
                  <span className="font-medium">{b.short_name}</span>
                  <span className="text-xs text-ink-muted">{b.purpose}</span>
                </button>
              ))}
            </PaletteGroup>
          )}

          {filteredDecisions.length > 0 && (
            <PaletteGroup label="Decisions">
              {filteredDecisions.map((d) => (
                <div
                  key={d.decision_id}
                  className="flex items-center gap-2 rounded-md px-3 py-2 hover:bg-canvas"
                >
                  <span className="flex-1 text-xs text-ink">{d.question}</span>
                  <button
                    type="button"
                    className="rounded border border-success/50 bg-success/10 px-2 py-0.5 text-[10px] font-semibold text-success hover:bg-success/20"
                    onClick={() => {
                      onDecisionAction(d.decision_id, 'approve');
                      onClose();
                    }}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="rounded border border-border bg-canvas px-2 py-0.5 text-[10px] text-ink-muted hover:text-ink"
                    onClick={() => {
                      onDecisionAction(d.decision_id, 'defer');
                      onClose();
                    }}
                  >
                    Defer
                  </button>
                </div>
              ))}
            </PaletteGroup>
          )}

          {filteredActions.length > 0 && (
            <PaletteGroup label="Actions">
              {filteredActions.map((a) => (
                <button
                  key={a.to}
                  type="button"
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-start text-sm text-ink hover:bg-canvas"
                  onClick={() => {
                    navigate(a.to);
                    onClose();
                  }}
                >
                  {a.label}
                </button>
              ))}
            </PaletteGroup>
          )}

          {filteredBrains.length === 0 &&
            filteredDecisions.length === 0 &&
            filteredActions.length === 0 && (
              <div className="flex h-16 items-center justify-center text-xs text-ink-subtle">
                No results for "{query}"
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

function PaletteGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-2">
      <div className="px-3 pb-1 pt-2 text-[10px] uppercase tracking-wider text-ink-subtle">
        {label}
      </div>
      {children}
    </div>
  );
}
